import { useEffect, useState } from 'react'
import { correspondentService } from '../services/correspondent.service'
import { PermissionAction, permissionsService, PermissionType } from '../services/permissions.service'
import { Correspondent } from '../types'

let globalCache: Correspondent[] | null = null
let globalCachePromise: Promise<Correspondent[]> | null = null
let cacheListeners: Set<() => void> = new Set()

const loadCache = async (): Promise<Correspondent[]> => {
  if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
    return []
  }
  if (globalCache) {
    return globalCache
  }

  if (globalCachePromise) {
    return globalCachePromise
  }

  globalCachePromise = correspondentService
    .listAll()
    .then((response) => {
      globalCache = response.results || []
      globalCachePromise = null
      cacheListeners.forEach((listener) => listener())
      return globalCache
    })
    .catch(() => {
      globalCachePromise = null
      return []
    })

  return globalCachePromise
}

export const useCorrespondentsCache = (): { correspondents: Correspondent[]; loaded: boolean } => {
  const [correspondents, setCorrespondents] = useState<Correspondent[]>(globalCache || [])
  const [loaded, setLoaded] = useState(!!globalCache)

  useEffect(() => {
    if (globalCache) {
      setCorrespondents(globalCache)
      setLoaded(true)
      return
    }

    const listener = () => {
      if (globalCache) {
        setCorrespondents(globalCache)
        setLoaded(true)
      }
    }

    cacheListeners.add(listener)

    loadCache().then((cached) => {
      setCorrespondents(cached)
      setLoaded(true)
    })

    return () => {
      cacheListeners.delete(listener)
    }
  }, [])

  return { correspondents, loaded }
}
